import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import {
  addRentalDefaultToEvent,
  loadEventInfo,
  setExtras,
} from "../interfaces/eventInterface";
import { getIcon } from "./atoms/FuzzyIcon";
import CustomDescription from "./atoms/CustomDescription";
import { Button, Row, Col, Card, Divider, Select, Input, Tag, message, Spin } from "antd";
import CardView from "./atoms/CardView";
import Heading from "./atoms/Heading";
import { findLastKey } from "lodash";
import { BackwardOutlined, CloseCircleFilled, CloseCircleOutlined, CloudDownloadOutlined, DownloadOutlined, EditFilled, FilePdfOutlined, FolderOpenFilled, SettingOutlined } from "@ant-design/icons";
import KeyValueCollector from "./atoms/KeyValueCollector";
import AnalyticsGrid from "./analytics/AnalyticsGrid";
import { Link } from "react-router-dom";
import SearchSelect from "./atoms/SearchSelectWithDropdown";
import { FormNavigator } from "../dynamicform/formNavigator";
import { safeParse, toDayjs ,isoDateToDate } from "../helpers/helper";
import TenantCard from "./atoms/rentlog/TenantCard";
import EventInfoCard from "./atoms/rentlog/EventCard";
import SectionHeader from "./atoms/SectionHeader";
import TimelineLogger from "./atoms/TimelineLogger";
import EventSubForm from "./atoms/rentlog/EventSubForm";
import { deleteAgreementItem, updateAgreement } from "../interfaces/agreementInterface";
import CenteredDisplay, { Loader } from "./atoms/CenteredContent";
import ActionsMenu from "./atoms/ActionsMenu";
import { generateInvoice } from "../interfaces/invoiceBuilderInterface";
import { setInvoiceConfigs } from "../features/workspaceSlice";

const EventProductHeader = ({
  eventInfo,
  eventId,
  formNavigator,
  selectedIds,
  setLoader,
  icon, // 👈 optional header icon (ReactNode)
}) => {
  // 👇 default to ADD mode ON
  const [add, setAdd] = useState(true);
  const [search, setSearch] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const dispatch = useDispatch();


  const resetModes = () => {
    setAdd(false);
    setSearch(false);
  };

  // === DEFAULT HEADER (NO ADD / SEARCH) ===
  if (!add && !search) {
    return (
      <div className="flex items-center justify-between py-1">
        {/* LEFT: Title */}
        <div className="flex flex-col gap-0.5">
          <Divider
            orientation="left"
            plain
            className="!m-0 !border-gray-100"
          >
            <div className="flex items-center gap-2">
              {icon && (
                <span className="flex items-center text-slate-500">
                  {icon}
                </span>
              )}
              <span className="text-sm font-semibold text-slate-700">
                Rental Basket
              </span>
            </div>
          </Divider>
          {eventInfo?.name && (
            <span className="text-[11px] text-slate-400 ml-3">
              For event: <span className="font-medium">{eventInfo.name}</span>
            </span>
          )}
        </div>

        {/* RIGHT: Actions */}
        <div className="flex items-center gap-2">
          <Button
            size="small"
            className="rounded-full bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100 hover:border-slate-300"
            onClick={() => {
              setSearch(true);
              setAdd(false);
            }}
          >
            Search
          </Button>
          <Button
            size="small"
            type="primary"
            className="rounded-full bg-emerald-500 border-emerald-500 hover:bg-emerald-600"
            onClick={() => {
              setSearch(false);
              setAdd(true);
            }}
          >
            Add
          </Button>
        </div>
      </div>
    );
  }

  // === COMMON TOP BAR FOR ADD / SEARCH MODES ===
  const ModeHeader = ({ label }) => (
    <div className="flex items-center justify-between mb-2">
      <div className="flex items-center gap-2">
        {icon && (
          <span className="flex items-center text-slate-500 text-sm">
            {icon}
          </span>
        )}
        <span className="text-[11px] font-medium uppercase tracking-wide text-slate-500">
          {label}
        </span>
      </div>
      <Button
        size="small"
        className="rounded-full bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100 hover:border-slate-300"
        onClick={resetModes}
      >
        ← Back
      </Button>
    </div>
  );

  // === ADD MODE ===
  if (add) {
    return (
      <div className="py-2">
        <ModeHeader label="Add rental / product to basket" />
        <div className="rounded-lg bg-slate-50/70 border border-slate-100 px-2 py-2">
          <Row gutter={8} align="middle">
            <Col span={24}>
              <SearchSelect
                placeholder="Search to start fetching rental/products"
                field="rental.name,onlyOneAgreement"

                selectedIds={selectedIds}

                extras={{
                  "Add":(rentalId)=>(<EventSubForm maxEndDate={new Date(eventInfo?.endDate)} minStartDate={new Date(eventInfo?.startDate)} onClick={
                    async (item) => {
                      setLoader(true)
                    await addRentalDefaultToEvent(dispatch, {
                      eventId: eventId,
                      tenantId: eventInfo.tenantId,
                      rentalId: rentalId,
                      startDate: eventInfo.startDate,
                      agreementId:eventInfo.agreementId,
                      endDate: eventInfo.endDate,
                      amount: -1,
                      quantity:1,
                      workspaceId:eventInfo.workspaceId,
                      status: "Active",
                      ...item
                    });
                    await loadEventInfo(dispatch, eventId);
                    setLoader(false)
                  }} values={{
                      eventId: eventId,
                      tenantId: eventInfo.tenantId,
                      agreementId:eventInfo.agreementId,
                      startDate: eventInfo.startDate,
                      endDate: eventInfo.endDate,
                      amount: -1,
                      quantity:1,
                      workspaceId:eventInfo.workspaceId,
                      status: "Active",
                    }} />)
                }}
              />
            </Col>
          </Row>
        </div>
      </div>
    );
  }

  // === SEARCH MODE ===
  if (search) {
    return (
      <div className="py-2">
        <ModeHeader label="Search in rental basket" />
        <div className="rounded-lg bg-slate-50/70 border border-slate-100 px-2 py-2">
          <Row gutter={8} align="middle">
            <Col span={24}>
              <Input
                size="medium"
                className="rounded-lg bg-white border-slate-200 focus:border-emerald-500"
                placeholder="Type to search..."
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
              />
            </Col>
          </Row>
        </div>
      </div>
    );
  }

  return null;
};


const EventView = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const eventInfo = useSelector((state) => state.rent.eventInfo);
  const invoiceConfigs = useSelector(state=>state.workspace.invoiceConfigs)
  const defaultInvoiceConfig = useSelector(state=>state.workspace.defaultInvoiceConfig)
  const [selectedInvoiceConfig, setSelectedInvoiceConfig] = useState(defaultInvoiceConfig)
  const shouldShowAnalytics = useSelector((state)=>state.workspace?.role?.sensitiveInfoBlock?.analytics || false)
  const eventAgreements = useSelector(
    (state) => state.rent.eventAgreements.data
  );
  const [loader , setLoader] = useState(false)
  const total = useSelector((state) => state.rent.eventAgreements.total);
  const currentPage = useSelector(
    (state) => state.rent.eventAgreements.currentPage
  );
  const extras = eventInfo.extras;
  const formNavigator = FormNavigator(dispatch)
  useEffect(() => {
    loadEventInfo(dispatch, id);
  }, []);
   useEffect(() => {
    if (!defaultInvoiceConfig && invoiceConfigs && invoiceConfigs.length > 0){
      setSelectedInvoiceConfig(invoiceConfigs?.[0]?.['id'])
    }
  }, [invoiceConfigs]);
  const onSaveExtras = (key, values) => {
    var extrasTemp = safeParse(extras) || {};
    if(values){
    extrasTemp[key] = values;
    setExtras(dispatch, id, JSON.stringify(extrasTemp));
    }
  };

  const data =
    (eventAgreements &&
    Object.keys(eventAgreements).length > 0) ?
    Object.values(eventAgreements)?.flat() : [];
  if (Object.keys(eventInfo).length == 0){
    return (<Loader/>)
  }
  return (
    <>
    <Spin spinning={loader} description={"Please wait , While we update your event"}  size="large" percent={"auto"}>
      <div style={{ position: "relative" }}>
        <Link to={"/events"}>
          <Button
            style={{
              padding: 6,
              height: 20,
              position: "absolute",
              left: -5,
              top: -5,
            }}
            variant="filled"
            color="danger"
          >
            <BackwardOutlined title="back" />
          </Button>
        </Link>

        <EventInfoCard
          extra={
            <>
              <Button
                size="small"
                className="ml-1"
                icon={<EditFilled />}
                onClick={() =>
                  formNavigator.event(id, {
                    tenantId: eventInfo.tenantId,
                    name: eventInfo.name,
                    description: eventInfo.description,
                    startDate: eventInfo.startDate,
                    endDate: eventInfo.endDate,
                  })
                }
              >
                Edit
              </Button>
              { eventInfo.agreementId &&
              <Button
                className="ml-1"
                size="small"
                icon={
                  eventInfo.status == "Active" ? <CloseCircleOutlined /> : <FolderOpenFilled/>
                }
                onClick={async () => {
                  await updateAgreement({id:eventInfo.agreementId ,status:eventInfo.status == "Active" ? 'Inactive' : 'Active'})
                  await loadEventInfo(dispatch, id);
                }}
              >
                {eventInfo.status == "Active" ? "Close Event" : "Re-Open Event"}
              </Button>}
              { eventInfo.agreementId &&
              <ActionsMenu

                    configs={[

                      {
                        label: (
                          <div
                            className="flex items-center justify-between gap-3 px-3 py-2 rounded-lg border border-gray-200 bg-gray-50"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <div className="flex flex-col">
                              <span className="text-sm font-medium text-gray-900">
                                Choose any config
                              </span>
                            </div>

                            <Select
                              className="w-[220px]"
                              size="middle"
                              placeholder="Select config"
                              options={invoiceConfigs.map((item) => ({
                                label: item.configName,
                                value: item.id,
                              }))}
                              value={selectedInvoiceConfig}
                              onChange={(val) => setSelectedInvoiceConfig(val)}

                            />
                          </div>
                        ),


                      },

                      {
                        label: (
                          <span className="flex items-center gap-2">
                            <FilePdfOutlined /> Download Invoice
                          </span>
                        ),
                        callback: (record) => {
                          if (selectedInvoiceConfig){
                          generateInvoice(
                            eventInfo.agreementId,
                            selectedInvoiceConfig,
                          );

                          message.success("Getting Invoice Ready , You will be redirected to view mode and You can download from there")
                        } else {
                          message.error("No valid invoice config selected , Add it in invoice builder")
                        }
                        },
                      },
                      {
                        label: (
                          <span className="flex items-center gap-2">
                            <FilePdfOutlined /> Regenrate & Download Invoice
                          </span>
                        ),
                        callback: (record) => {
                          if(selectedInvoiceConfig){
                          generateInvoice(
                            eventInfo.agreementId,
                            selectedInvoiceConfig,
                            true,
                          );

                          message.success("Getting Invoice Ready , You will be redirected to view mode and You can download from there")
                        }else {
                          message.error("No valid invoice config selected , Add it in invoice builder")
                        }
                        }
                      },
                    ]}
                    title={
                      <Button
                className="ml-1"
                size="small"
                type="primary"
                icon={<CloudDownloadOutlined />}
                onClick={() => {}}
              >
                Generate Invoice
              </Button>
                    }
                  />}

            </>
          }
          heading={getIcon("Event", true, { size: 24, title: "Event" })}
          name={eventInfo?.name}
          description={eventInfo?.description}
          subtitle={`${isoDateToDate(
            eventInfo.startDate,
            false,
          )} - ${isoDateToDate(eventInfo.endDate, false)}`}
        />
      </div>

      {shouldShowAnalytics && data && data.length > 0 && (
        <AnalyticsGrid
          config={"events"}
          data={{
            eventInfo,
            eventAgreements: { data: data, total: total },
          }}
        />
      )}
      {eventInfo.tenant && (
        <>
          <SectionHeader
            icon={getIcon("Tenant", true, { size: 24 })}
            title={"Assigned Tenants"}
          />
          {eventInfo.tenant && (
            <TenantCard showExpandIcon={false} tenant={eventInfo.tenant} />
          )}
        </>
      )}

      <Card
        title={
          <EventProductHeader
            eventId={id}
            formNavigator={formNavigator}
            selectedIds={data && data.map((item) => item?.rentalId)}
            eventInfo={eventInfo}
            setLoader={setLoader}
          />
        }
        style={{ marginTop: 10, height: "fit-content" }}
      >
        <CardView
          config={"event"}
          data={data}
          totalData={total}
          page={currentPage}
          view="ListView"
          availablePages={
            eventAgreements && new Set(Object.keys(eventAgreements))
          }
          callbacks={{
            onEdit: async (values) => {
              setLoader(true)
              await addRentalDefaultToEvent(dispatch, values);
              await loadEventInfo(dispatch, id, false);
              setLoader(false)
            },
            onDelete: async (record) => {
              await deleteAgreementItem(record);
              await loadEventInfo(dispatch, id, false);
            },
          }}
        />
      </Card>

      {extras && (
        <TimelineLogger
          data={safeParse(extras)}
          onSave={(values) => {
            onSaveExtras("timeline", values);
          }}
          title={"Log your Events in Timeline"}
        />
      )}

      <Row>
        <Col span={12}>
          <KeyValueCollector
            id={"expense"}
            key={"expense"}
            title={getIcon("expenses", true, {
              title: "Other Expenses",
              size: 24,
            })}
            config={"expenses"}
            data={extras}
            onSave={(values) => onSaveExtras("expenses", values)}
          />
        </Col>
        <Col span={12}>
          <KeyValueCollector
          id={"contacts"}
            key={"contacts"}
            title={getIcon("contacts", true, {
              title: "Other Contacts",
              size: 24,
            })}
            config={"contacts"}
            data={extras}
            onSave={(values) => onSaveExtras("contacts", values)}
          />
        </Col>
      </Row>
      </Spin>
    </>
  );
};

export default EventView;

